import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';

/**
 * Hook to manage the personalized AI insights setting
 * Syncs with Firebase user data (disableInsights field)
 * @returns Object with enabled state, setter function, and loading state
 */
export const usePersonalizedInsights = () => {
  const { currentUser } = useAuth();
  const [personalizedInsightsEnabled, setPersonalizedInsightsEnabledState] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load setting from Firebase on mount
  useEffect(() => {
    const loadSetting = async () => {
      if (!currentUser?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await userService.getUserData(currentUser.uid);
        // disableInsights: true means insights are disabled, so we invert it
        const enabled = userData?.disableInsights !== true;
        setPersonalizedInsightsEnabledState(enabled);
      } catch (error) {
        console.error('Error loading personalized insights setting:', error);
        // Default to enabled on error
        setPersonalizedInsightsEnabledState(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadSetting();
  }, [currentUser?.uid]);

  // Update setting and save to Firebase
  const setPersonalizedInsightsEnabled = useCallback(async (enabled: boolean) => {
    // Update local state immediately for responsiveness
    setPersonalizedInsightsEnabledState(enabled);

    if (!currentUser?.uid) {
      console.warn('Cannot save personalized insights setting: no user logged in');
      return;
    }

    try {
      // disableInsights: true means insights are disabled, so we invert the enabled value
      await userService.updateUserProfile(currentUser.uid, {
        disableInsights: !enabled,
      });
      console.log(`Personalized insights ${enabled ? 'enabled' : 'disabled'} for user ${currentUser.uid}`);
    } catch (error) {
      console.error('Error saving personalized insights setting:', error);
      // Revert on error
      setPersonalizedInsightsEnabledState(!enabled);
    }
  }, [currentUser?.uid]);

  return {
    personalizedInsightsEnabled,
    setPersonalizedInsightsEnabled,
    isLoading,
  };
};

/**
 * Utility function to check if personalized insights are enabled
 * This requires a userId and fetches from Firebase
 * @param userId - The user's ID
 * @returns Promise<boolean> indicating if personalized insights are enabled
 */
export const getPersonalizedInsightsEnabled = async (userId: string): Promise<boolean> => {
  try {
    const userData = await userService.getUserData(userId);
    return userData?.disableInsights !== true;
  } catch (error) {
    console.error('Error checking personalized insights setting:', error);
    return true; // Default to enabled on error
  }
};
